import axios from "axios";
import { fetchAuthSession, signOut } from "aws-amplify/auth";

const SESSION_EXPIRED_KEY = "sessionExpired";

const setSessionExpiredFlag = () => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
};

export const clearSessionExpiredFlag = () => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
};

export const getSessionExpiredFlag = () => {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
};

let refreshPromise: Promise<string | null> | null = null;

export const setupAxiosAuthInterceptor = () => {
  const interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const originalRequest = error?.config;

      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          if (!refreshPromise) {
            refreshPromise = fetchAuthSession({ forceRefresh: true })
              .then(({ tokens }) => tokens?.idToken?.toString() ?? null)
              .finally(() => {
                refreshPromise = null;
              });
          }
          const token = await refreshPromise;
          if (token) {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`,
            };
            return axios(originalRequest);
          }
        } catch (refreshError) {
          // Fall through to sign out on refresh failure.
        }

        setSessionExpiredFlag();
        await signOut();
      } else if (status === 403) {
        const detail =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "You do not have access to perform this action.";
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("auth:forbidden", { detail })
          );
        }
      }

      return Promise.reject(error);
    }
  );

  return () => axios.interceptors.response.eject(interceptorId);
};
